import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { USE_CASES } from "@/data/useCases";
import { ArrowUpRight, Search } from "lucide-react";

const TAGS = ["All", "Contract", "Finance", "Real Estate", "Hiring", "Coverage"];

export default function UseCaseIndex() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("All");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return USE_CASES.filter((u) => {
      if (tag !== "All" && u.tag !== tag) return false;
      if (!s) return true;
      return (
        u.label.toLowerCase().includes(s) ||
        u.for_who.toLowerCase().includes(s) ||
        u.checks.join(" ").toLowerCase().includes(s)
      );
    });
  }, [q, tag]);

  return (
    <section
      data-testid="use-case-index"
      className="max-w-[1280px] mx-auto px-6 md:px-10 py-14"
    >
      <div className="eyebrow mb-5">Playbooks</div>
      <h1 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
        Every review, hand-crafted.
      </h1>
      <p className="mt-4 text-ink-70 max-w-2xl">
        Each playbook is opinionated about what to check, quotes your document, and
        produces a copy-ready pack.
      </p>

      <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-2 border border-ink-20 px-3 py-2 bg-surface flex-1 max-w-md">
          <Search className="w-4 h-4 text-ink-50" />
          <input
            data-testid="usecase-search"
            className="bg-transparent outline-none w-full text-sm"
            placeholder="Search playbooks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => (
            <button
              key={t}
              data-testid={`usecase-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTag(t)}
              className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest border transition-colors ${
                tag === t
                  ? "bg-ink text-white border-ink"
                  : "text-ink-70 border-ink-20 hover:border-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-ink-10">
        {filtered.map((u, i) => (
          <button
            key={u.key}
            data-testid={`usecase-card-${u.key}`}
            onClick={() => navigate(`/use-cases/${u.key}`)}
            className={`text-left p-8 bg-surface hover:bg-[#FAFAF8] transition-colors group border-ink-10 ${
              i % 3 !== 2 ? "lg:border-r" : ""
            } ${i % 2 !== 1 ? "md:border-r lg:border-r" : ""} border-b`}
          >
            <div className="flex items-start justify-between mb-6">
              <span className="font-mono-plex text-xs text-ink-50 tracking-widest">
                {u.number}
              </span>
              <span className="font-mono-plex text-xs text-ink-50 uppercase tracking-widest">
                {u.tag}
              </span>
            </div>
            <div className="font-serif-display text-2xl text-ink mb-3 group-hover:text-forest transition-colors">
              {u.label}
            </div>
            <div className="text-sm text-ink-70 mb-5 leading-relaxed">{u.for_who}</div>
            <div className="flex items-center gap-2 text-xs font-mono-plex text-forest uppercase tracking-widest">
              Open playbook <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="p-16 text-center text-ink-50 col-span-full">
            No playbook matches that search.
          </div>
        )}
      </div>
    </section>
  );
}
